<template>
  <Card variant="outline" size="medium">
    <View gap="md" direction="column">
      <View>
        <Text variant="h4">{{ application.renter.user.name }}</Text>
        <Text v-if="application.type === ApplicantType.CO_SIGNER" color="tertiary" variant="h4">&nbsp;∙ Co-signer</Text>
      </View>

      <RenterProfileApplicationArtifact
        v-if="application.type !== ApplicantType.CO_SIGNER"
        title="Application"
        description="Get a complete view of the renter's personal info, job history, and income."
      >
        <Button @click="showSelfReportModal" class="artifact-action" size="small" variant="outline">View</Button>
      </RenterProfileApplicationArtifact>

      <RenterProfileApplicationArtifact
        :validityText="reportsExpiration.validityText"
        :expired="reportsExpiration.expired"
        title="Credit report"
        description="View the renter's credit score and history through TransUnion for clear financial insights."
      >
        <Button
          :to="renterTransUnionReportRoute(application.id, TransUnionProductType.CREDIT)"
          v-if="!reportsExpiration.expired"
          class="artifact-action"
          size="small"
          variant="outline"
        >
          View
        </Button>
      </RenterProfileApplicationArtifact>

      <RenterProfileApplicationArtifact
        v-if="application.type !== ApplicantType.CO_SIGNER"
        :validityText="reportsExpiration.validityText"
        :expired="reportsExpiration.expired"
        title="Criminal report"
        description="Review a detailed criminal history provided by the renter, verified through TransUnion."
      >
        <Button
          :to="renterTransUnionReportRoute(application.id, TransUnionProductType.CRIMINAL)"
          v-if="!reportsExpiration.expired"
          class="artifact-action"
          size="small"
          variant="outline"
        >
          View
        </Button>
      </RenterProfileApplicationArtifact>

      <RenterProfileApplicationArtifact
        v-if="application.type !== ApplicantType.CO_SIGNER"
        :validityText="reportsExpiration.validityText"
        :expired="reportsExpiration.expired"
        title="Eviction report"
        description="Review a detailed eviction history provided by the renter, verified through TransUnion."
      >
        <Button
          :to="renterTransUnionReportRoute(application.id, TransUnionProductType.EVICTION)"
          v-if="!reportsExpiration.expired"
          class="artifact-action"
          size="small"
          variant="outline"
        >
          View
        </Button>
      </RenterProfileApplicationArtifact>
    </View>

    <RenterProfileSelfReportModal :application="application" ref="applicationSelfReportModalRef" />
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import {
  ApplicantType,
  ApplicationWithRenterDto,
  TransUnionProductType,
  getApplicationReportExpirationData,
} from '@tallo/applications';
import { Button, Card, Text, View } from '@tallo/design-system';
import { renterTransUnionReportRoute } from '@tallo/investor/navigation';

import RenterProfileApplicationArtifact from './renter-profile-application-artifact.vue';
import RenterProfileSelfReportModal from './renter-profile-self-report-modal.vue';

const applicationSelfReportModalRef = ref<InstanceType<typeof RenterProfileSelfReportModal>>();
const props = defineProps<{ application: ApplicationWithRenterDto }>();
const reportsExpiration = computed(() => getApplicationReportExpirationData(props.application.expiresAt));

function showSelfReportModal(): void {
  applicationSelfReportModalRef.value?.modalRef?.show();
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.artifact-action {
  width: 100%;
  max-width: 24rem;
  align-self: center;
}

@include screen-min-sm {
  .artifact-action {
    width: fit-content;
  }
}
</style>
